/**
 * Column mapping for the "Excel ile Toplu Yükle" flow in Sistem Kullanıcıları.
 *
 * Kept out of the component so the mapping — which has to cope with whatever
 * header spelling the admin's spreadsheet happens to use — can be unit tested
 * without Firebase.
 */

export type ImportedUserRole = 'admin' | 'catalog_manager' | 'designer';

export interface ImportedUserRow {
  name: string;
  email: string;
  role: ImportedUserRole;
  /** Empty when the sheet has no phone column (or the cell is blank) and the
   *  matched sales point has no phone either; the user then falls back to their
   *  assigned sales point's phone on the cover. */
  phone: string;
  /** Canonical sales point name (as stored in Firestore), or '' when the sheet
   *  has no such column or the value matched nothing. */
  assignedSalesPoint: string;
}

/** The subset of a sales point this mapping needs. */
export interface SalesPointOption {
  name: string;
  phone: string;
}

export interface UserImportResult {
  rows: ImportedUserRow[];
  /** False when the sheet lacks a name or e-mail column — nothing can be imported. */
  hasRequiredColumns: boolean;
  /** Distinct sales point values from the sheet that matched no known sales
   *  point; those users are imported without an assignment. */
  unmatchedSalesPoints: string[];
}

const TURKISH_FOLD: Record<string, string> = {
  'ı': 'i', 'i̇': 'i', 'ş': 's', 'ğ': 'g', 'ü': 'u', 'ö': 'o', 'ç': 'c', 'â': 'a', 'î': 'i', 'û': 'u',
};

/** Lowercases (Turkish-aware), folds Turkish diacritics and drops spaces, `_`
 *  and `-`, so "Satış Noktası", "satis_noktasi" and "SATIŞNOKTASI" all match. */
const normalizeHeader = (header: string): string =>
  header
    .toLocaleLowerCase('tr')
    .replace(/[\s_-]/g, '')
    .split('')
    .map(ch => TURKISH_FOLD[ch] ?? ch)
    .join('');

/** Finds the first header of `row` matching any candidate, ignoring case, spaces, `_` and `-`. */
export function findColumnKey(row: Record<string, unknown>, candidates: string[]): string | null {
  const keys = Object.keys(row);
  for (const candidate of candidates) {
    const found = keys.find(k => normalizeHeader(k) === normalizeHeader(candidate));
    if (found) return found;
  }
  return null;
}

const NAME_KEYS = ['adsoyad', 'adisoyadi', 'isim', 'name', 'fullname', 'kullaniciadi'];
const EMAIL_KEYS = ['eposta', 'email', 'mail', 'emailadresi'];
const ROLE_KEYS = ['rol', 'role', 'yetki', 'durum'];
const PHONE_KEYS = ['telefon', 'telefonno', 'telefonnumarasi', 'tel', 'gsm', 'ceptelefonu', 'phone', 'phonenumber', 'mobile'];
const SALES_POINT_KEYS = ['satisnoktasi', 'satisnokta', 'magaza', 'bayi', 'sube', 'salespoint', 'store', 'storename', 'atanmissatisnoktasi'];

/** Sales point names are matched loosely: case, spaces and Turkish dotted/dotless
 *  i differences in the admin's spreadsheet should not lose an assignment. */
const normalizeSalesPointName = (value: string): string =>
  value.toLocaleLowerCase('tr').replace(/\s+/g, ' ').trim();

function parseRole(value: unknown): ImportedUserRole {
  const roleVal = String(value ?? '').toLowerCase().trim();
  if (roleVal === 'admin' || roleVal === 'yönetici' || roleVal === 'yonetici') return 'admin';
  if (roleVal === 'catalog_manager' || roleVal === 'katalog yöneticisi' || roleVal === 'katalogyoneticisi' || roleVal === 'katalog') {
    return 'catalog_manager';
  }
  return 'designer';
}

/**
 * Maps every sheet row (not just the first) to a user record. Rows without both
 * a name and an e-mail are skipped; the role defaults to `designer`. A sales
 * point value is only kept when it matches one of `salesPoints` — unmatched
 * values are reported instead of being written as free text, so the proposal
 * cover's sales point lookup never silently misses.
 */
export function parseUserImportRows(
  rows: Record<string, unknown>[],
  salesPoints: SalesPointOption[] = []
): UserImportResult {
  if (rows.length === 0) return { rows: [], hasRequiredColumns: false, unmatchedSalesPoints: [] };

  const firstRow = rows[0];
  const nameKey = findColumnKey(firstRow, NAME_KEYS);
  const emailKey = findColumnKey(firstRow, EMAIL_KEYS);
  const roleKey = findColumnKey(firstRow, ROLE_KEYS);
  const phoneKey = findColumnKey(firstRow, PHONE_KEYS);
  const salesPointKey = findColumnKey(firstRow, SALES_POINT_KEYS);

  if (!nameKey || !emailKey) return { rows: [], hasRequiredColumns: false, unmatchedSalesPoints: [] };

  const salesPointsByName = new Map(salesPoints.map(sp => [normalizeSalesPointName(sp.name), sp]));
  const unmatched: string[] = [];

  const parsed: ImportedUserRow[] = [];
  for (const row of rows) {
    const name = String(row[nameKey] ?? '').trim();
    const email = String(row[emailKey] ?? '').trim().toLowerCase();
    if (!name || !email) continue;

    const rawSalesPoint = salesPointKey ? String(row[salesPointKey] ?? '').trim() : '';
    const matchedSalesPoint = rawSalesPoint ? salesPointsByName.get(normalizeSalesPointName(rawSalesPoint)) : undefined;
    if (rawSalesPoint && !matchedSalesPoint && !unmatched.includes(rawSalesPoint)) {
      unmatched.push(rawSalesPoint);
    }

    // Excel usually stores phone numbers as text, but a plain digit string
    // ("5551234567") comes back as a number — String() normalises both.
    const rawPhone = phoneKey ? String(row[phoneKey] ?? '').trim() : '';
    // Same default as the "Kullanıcı Tanımla" form: no phone of their own means
    // the matched sales point's phone is written to the user.
    const phone = rawPhone || matchedSalesPoint?.phone || '';

    parsed.push({
      name,
      email,
      role: roleKey ? parseRole(row[roleKey]) : 'designer',
      phone,
      assignedSalesPoint: matchedSalesPoint?.name || '',
    });
  }

  return { rows: parsed, hasRequiredColumns: true, unmatchedSalesPoints: unmatched };
}
